import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CloudUpload, Loader2, FolderOpen, X } from 'lucide-react';
import { MAIL_API, mailAuth } from './mailUtils';

/** Popover that resolves a WO / quote number to its Drive folder (+ subfolders) and confirms a save. */
export const DriveFolderPopover = ({ open, onClose, anchorRef, chips = [], confirmLabel, saving, onConfirm, testid = 'mail-save-drive' }) => {
  const [ref, setRef] = useState(chips[0]?.ref || '');
  const [typed, setTyped] = useState('');
  const [target, setTarget] = useState(null);
  const [folderId, setFolderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 300;
    setPos({ left: Math.min(r.left, window.innerWidth - 300), top: up ? undefined : r.bottom + 6, bottom: up ? window.innerHeight - r.top + 6 : undefined });
  }, [open, anchorRef]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);
  useEffect(() => {
    if (!open || !ref) { setTarget(null); return undefined; }
    let alive = true;
    setLoading(true); setTarget(null);
    axios.get(`${MAIL_API}/drive-targets/${encodeURIComponent(ref)}`, mailAuth())
      .then(r => { if (alive) { setTarget(r.data); setFolderId(r.data.id); } })
      .catch(e => { if (alive) toast.error(e.response?.data?.detail || 'Could not find the job folder'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, ref]);

  if (!open || !pos) return null;
  return createPortal(
    <div className="fixed z-[9500] w-72 rounded-xl border border-slate-200 bg-white shadow-2xl p-3 space-y-2" style={pos} data-testid={`${testid}-popover`} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between"><span className="text-xs font-bold text-slate-900">Save to job folder</span><button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-3.5 h-3.5" /></button></div>
      <div className="flex flex-wrap gap-1">
        {chips.map(c => <button key={c.ref} onClick={() => setRef(c.ref)} className={`text-[11px] font-mono font-bold px-2 py-1 rounded-md border ${ref === c.ref ? 'bg-sky-600 border-sky-600 text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-testid={`${testid}-ref-${c.ref}`}>{c.ref}</button>)}
        <form onSubmit={e => { e.preventDefault(); if (typed.trim()) setRef(typed.trim().toUpperCase()); }} className="flex-1 min-w-[110px]">
          <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="or WO / quote #" className="w-full text-[11px] bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-500" data-testid={`${testid}-typed`} />
        </form>
      </div>
      {loading && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Finding the folder…</p>}
      {target && (
        <>
          <p className="text-[11px] text-slate-700 truncate flex items-center gap-1"><FolderOpen className="w-3 h-3 text-amber-500" />{target.label}: <a href={target.url} target="_blank" rel="noreferrer" className="underline text-sky-700 truncate">{target.name}</a></p>
          <select value={folderId} onChange={e => setFolderId(e.target.value)} className="w-full text-xs bg-white text-slate-900 rounded-md px-2 py-1.5 border border-slate-300" data-testid={`${testid}-folder`}>
            <option value={target.id}>Job folder (top level)</option>
            {target.subfolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <button onClick={() => onConfirm(folderId, target, ref)} disabled={saving} className="w-full py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 text-white text-xs font-bold flex items-center justify-center gap-1.5" data-testid={`${testid}-confirm`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}{saving ? 'Saving…' : confirmLabel}
          </button>
        </>
      )}
      {!ref && !loading && <p className="text-[11px] text-slate-500">No job number on this thread — type the WO or quote number above.</p>}
    </div>, document.body);
};

/** "Drive" button for one attachment. */
export const SaveToDrive = ({ attachment, chips = [] }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const btnRef = useRef(null);
  const save = async (folderId, target, ref) => {
    setSaving(true);
    try {
      const r = await axios.post(`${MAIL_API}/attachments/${attachment.message_id}/${attachment.id}/to-drive`,
        { folder_id: folderId, name: attachment.filename, mime: attachment.mime, ref }, { ...mailAuth(), timeout: 120000 });
      const sub = target?.subfolders?.find(f => f.id === folderId);
      toast.success(`Saved ${r.data.name} to ${target?.label} › ${sub ? sub.name : target?.name}`, { action: { label: 'Open', onClick: () => window.open(r.data.link, '_blank') } });
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save to Drive'); }
    setSaving(false);
  };
  return (
    <span className="relative inline-flex">
      <button ref={btnRef} onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 px-1.5 py-1 rounded hover:bg-sky-50" title="Save to the job's Drive folder" data-testid={`mail-save-drive-${attachment.id.slice(0, 12)}`}>
        <CloudUpload className="w-3.5 h-3.5" />Drive
      </button>
      <DriveFolderPopover open={open} onClose={() => setOpen(false)} anchorRef={btnRef} chips={chips} saving={saving} onConfirm={save} confirmLabel={`Save ${attachment.filename}`} />
    </span>
  );
};
